import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Calculator,
  Calendar,
  AlertTriangle,
  Info 
} from 'lucide-react';
import type { CompensationRule, RateContext } from '@/services/compensation/types';
import { calculationEngine } from '@/services/compensation/calculationEngine';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';

interface EmployeeImpactPreviewProps {
  employeeId: string;
  employeeName: string;
  currentRules: CompensationRule[];
  newRule?: CompensationRule;
  payPeriodHours?: number;
  showDetailedBreakdown?: boolean;
  rateContext?: RateContext;
}

interface PayrollImpact {
  category: string;
  currentAmount: number;
  newAmount: number;
  difference: number;
  description: string;
}

export function EmployeeImpactPreview({
  employeeId,
  employeeName,
  currentRules,
  newRule,
  payPeriodHours = 80, // Default to 80 hours (2 weeks)
  showDetailedBreakdown = true,
  rateContext = {},
}: EmployeeImpactPreviewProps) {
  const { currencyCode } = useWorkplaceCurrency();

  const calculatePayrollImpact = (): PayrollImpact[] => {
    const impacts: PayrollImpact[] = [];
    const allRules = newRule ? [...currentRules, newRule] : currentRules;
    
    // Group rules by category
    const ruleCategories = {
      base_pay: allRules.filter(r => ['hourly_rate', 'salary'].includes(r.rule_type)),
      overtime: allRules.filter(r => r.rule_type === 'overtime'),
      allowances: allRules.filter(r => ['allowance', 'bonus'].includes(r.rule_type)),
      deductions: allRules.filter(r => r.rule_type === 'deduction'),
    };

    // Calculate base pay impact
    const currentBasePay = calculateBasePay(currentRules.filter(r => ['hourly_rate', 'salary'].includes(r.rule_type)), rateContext);
    const newBasePay = calculateBasePay(ruleCategories.base_pay, rateContext);
    
    if (currentBasePay !== newBasePay) {
      impacts.push({
        category: 'Base Pay',
        currentAmount: currentBasePay,
        newAmount: newBasePay,
        difference: newBasePay - currentBasePay,
        description: `${payPeriodHours} hours at updated rate`
      });
    }

    // Calculate overtime impact (assuming 10 hours overtime)
    const overtimeHours = 10;
    const currentOvertimePay = calculateOvertimePay(currentRules.filter(r => r.rule_type === 'overtime'), overtimeHours);
    const newOvertimePay = calculateOvertimePay(ruleCategories.overtime, overtimeHours);
    
    if (currentOvertimePay !== newOvertimePay) {
      impacts.push({
        category: 'Overtime',
        currentAmount: currentOvertimePay,
        newAmount: newOvertimePay,
        difference: newOvertimePay - currentOvertimePay,
        description: `${overtimeHours} overtime hours`
      });
    }

    // Calculate allowances impact
    const currentAllowances = calculateAllowances(currentRules.filter(r => ['allowance', 'bonus'].includes(r.rule_type)));
    const newAllowances = calculateAllowances(ruleCategories.allowances);
    
    if (currentAllowances !== newAllowances) {
      impacts.push({
        category: 'Allowances',
        currentAmount: currentAllowances,
        newAmount: newAllowances,
        difference: newAllowances - currentAllowances,
        description: 'Bonuses and allowances'
      });
    }

    // Calculate deductions impact
    const currentDeductions = calculateDeductions(currentRules.filter(r => r.rule_type === 'deduction'));
    const newDeductions = calculateDeductions(ruleCategories.deductions);
    
    if (currentDeductions !== newDeductions) {
      impacts.push({
        category: 'Deductions',
        currentAmount: currentDeductions,
        newAmount: newDeductions,
        difference: newDeductions - currentDeductions,
        description: 'Taxes, insurance, and other deductions'
      });
    }

    return impacts;
  };

  /**
   * Calculate base pay using proper salary-to-hourly conversion
   * FIX: Previously multiplied annual salary by hours, now converts salary to hourly rate first
   */
  const calculateBasePay = (rules: CompensationRule[], ctx: RateContext): number => {
    if (!rules.length) return 0;
    
    // Separate hourly and salary rules
    const hourlyRules = rules.filter(r => r.rule_type === 'hourly_rate' && r.is_active);
    const salaryRules = rules.filter(r => r.rule_type === 'salary' && r.is_active);
    
    // Use the calculation engine to get the correct effective hourly rate
    const effectiveRateResult = calculationEngine.getEffectiveRate(hourlyRules, salaryRules, ctx);
    
    // Now multiply the correct hourly rate by pay period hours
    return effectiveRateResult.rate * payPeriodHours;
  };

  const calculateOvertimePay = (rules: CompensationRule[], overtimeHours: number): number => {
    const activeRule = rules.find(r => r.is_active);
    if (!activeRule) return 0;
    
    const baseRate = 25; // Mock base rate
    const multiplier = activeRule.multiplier_rate || 1.5;
    return baseRate * multiplier * overtimeHours;
  };

  const calculateAllowances = (rules: CompensationRule[]): number => {
    return rules
      .filter(r => r.is_active)
      .reduce((total, rule) => {
        if (rule.calculation_method === 'fixed_amount') {
          return total + (rule.base_rate || 0);
        }
        return total;
      }, 0);
  };

  const calculateDeductions = (rules: CompensationRule[]): number => {
    return rules
      .filter(r => r.is_active)
      .reduce((total, rule) => {
        if (rule.calculation_method === 'fixed_amount') {
          return total + (rule.base_rate || 0);
        }
        return total;
      }, 0);
  };

  const impacts = calculatePayrollImpact();
  const totalCurrentPay = impacts.reduce((sum, impact) => 
    impact.category === 'Deductions' ? sum - impact.currentAmount : sum + impact.currentAmount, 0
  );
  const totalNewPay = impacts.reduce((sum, impact) => 
    impact.category === 'Deductions' ? sum - impact.newAmount : sum + impact.newAmount, 0
  );
  const totalDifference = totalNewPay - totalCurrentPay;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(Math.abs(amount));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="h-5 w-5" />
          Payroll Impact Preview
        </CardTitle>
        <div className="text-sm text-muted-foreground">
          Impact analysis for {employeeName}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary */}
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Net Pay Change</span>
            <div className="flex items-center gap-2">
              {totalDifference > 0 ? (
                <TrendingUp className="h-4 w-4 text-green-500" />
              ) : totalDifference < 0 ? (
                <TrendingDown className="h-4 w-4 text-red-500" />
              ) : (
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              )}
              <span className={`font-medium ${
                totalDifference > 0 ? 'text-green-600' : 
                totalDifference < 0 ? 'text-red-600' : 
                'text-muted-foreground'
              }`}>
                {totalDifference >= 0 ? '+' : ''}{formatCurrency(totalDifference)}
              </span>
            </div>
          </div>
          
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Current: {formatCurrency(totalCurrentPay)}</span>
            <span>New: {formatCurrency(totalNewPay)}</span>
          </div>
        </div>

        {/* Detailed Breakdown */}
        {showDetailedBreakdown && impacts.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Detailed Breakdown</h4>
            
            {impacts.map((impact, index) => (
              <div key={index} className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{impact.category}</span>
                    <Badge variant="outline" className="text-xs">
                      {impact.difference > 0 ? `+${formatCurrency(impact.difference)}` : 
                       impact.difference < 0 ? `-${formatCurrency(impact.difference)}` : 
                       'No Change'}
                    </Badge>
                  </div>
                  
                  {impact.difference !== 0 && (
                    <div className="flex items-center gap-1">
                      {impact.difference > 0 ? (
                        <TrendingUp className="h-3 w-3 text-green-500" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-red-500" />
                      )}
                    </div>
                  )}
                </div>
                
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Current: {formatCurrency(impact.currentAmount)}</span>
                  <span>New: {formatCurrency(impact.newAmount)}</span>
                </div>
                
                <p className="text-xs text-muted-foreground">{impact.description}</p>
              </div>
            ))}
          </div>
        )}

        {/* Pay Period Info */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Calendar className="h-3 w-3" />
          <span>Based on {payPeriodHours} hours per pay period</span>
        </div>

        {/* Warnings */}
        {newRule && (
          <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-yellow-800">
              <p className="font-medium mb-1">Preview Calculation</p>
              <p>This is an estimated impact based on current rules and typical work hours. 
                 Actual payroll may vary based on attendance, overtime, and other factors.</p>
            </div>
          </div>
        )}

        {impacts.length === 0 && (
          <div className="text-center text-muted-foreground py-4">
            <Info className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No payroll impact detected</p>
            <p className="text-xs">The current rule configuration will not affect this employee's pay</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}